import { c as _c } from "react/compiler-runtime";
import { BREAKPOINTS } from '@hookdeck/theme';
import { useContext } from 'react';
import styled from 'styled-components';
import { SourceTypeFeatureTypesUnmanaged } from '../../../../../../../../typings/Integration.interface';
import { APISource } from '../../../../../../../../typings/Source.interface';
import { stringifyQuery } from '../../../../../utils';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Icon, { IconName } from '../../../../common/base/Icon';
import Skeleton from '../../../../common/base/Skeleton';
import { ChartDataSet } from '../../../../common/Chart/Chart';
import { Div } from '../../../../common/helpers/StyledUtils';
import BaseMetricChart from '../../../../common/metrics/MetricChart';
import ChartInteractionGroup from '../../../../common/Chart/ChartInteractionGroup';
import { default_data_formatter } from '../../../../common/metrics/metric-definitions';
import useViewportBreakpoint from '../../../../hooks/useViewportBreakpoint';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataDescription, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardContext';
import MetricChart from '../../Metrics/MetricChart';
import MetricDatePicker, { getDateRangeQueryParam, useDateRange } from '../../Metrics/MetricDatePicker';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import { ChartData, MetricConfigs, useMetric } from '../../Metrics/useMetric';
const StyledOverviewMetricsGrid = styled(Div)`
  @media (max-width: 1400px) {
    grid-template-columns: minmax(0, 1fr) !important;
  }
`;
const overview_columns_for_breakpoints: Record<keyof typeof BREAKPOINTS, number> = {
  mobile: 1,
  tablet: 1,
  laptop: 2,
  desktop: 2
};
const toDataset = (data: ChartData | null | undefined, config: {
  key: string;
  label: string;
  sort_order?: number;
  metric_type: ChartDataSet['metric_type'];
  theme_color?: ChartDataSet['theme_color'];
  hex_color?: string;
  getDataLabel?: ChartDataSet['getDataLabel'];
}): ChartDataSet | null => {
  if (!data) {
    return null;
  }
  return {
    ...data,
    ...config
  };
};
const SourceOverviewMetrics: React.FC<{
  source_id: string;
}> = t0 => {
  const $ = _c(51);
  const {
    source_id
  } = t0;
  const breakpoint = useViewportBreakpoint();
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const [date_range, onDateRangeChanged] = useDateRange("last_hour");
  let t1;
  if ($[0] !== source_id) {
    t1 = {
      filters: {
        source_id: [source_id]
      }
    };
    $[0] = source_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== date_range || $[3] !== t1) {
    t2 = {
      date_range,
      options: t1
    };
    $[2] = date_range;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const metric_configs = t2;
  const {
    data: accepted_requests
  } = useMetric("chart:bar", "requests_accepted_count", refresh_key, metric_configs);
  const {
    data: rejected_requests
  } = useMetric("chart:bar", "requests_rejected_count", refresh_key, metric_configs);
  const requests_status_loading = accepted_requests === undefined || rejected_requests === undefined;
  let t3;
  bb0: {
    if (requests_status_loading) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[5] !== accepted_requests) {
      t4 = toDataset(accepted_requests, {
        key: "accepted",
        label: "Accepted",
        metric_type: "count",
        theme_color: "success",
        getDataLabel: default_data_formatter.count
      });
      $[5] = accepted_requests;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== rejected_requests) {
      t5 = toDataset(rejected_requests, {
        key: "rejected",
        label: "Rejected",
        metric_type: "count",
        theme_color: "danger",
        getDataLabel: default_data_formatter.count
      });
      $[7] = rejected_requests;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t4 || $[10] !== t5) {
      t6 = [t4, t5].filter(_temp);
      $[9] = t4;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t3 = t6;
  }
  const requests_status_datasets = t3;
  let t4;
  if ($[12] !== date_range || $[13] !== onDateRangeChanged) {
    t4 = <MetricDatePicker date_range={date_range} onDateRangeChanged={onDateRangeChanged} />;
    $[12] = date_range;
    $[13] = onDateRangeChanged;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== updateRefreshKey) {
    t5 = <Button outline={true} icon="reload" onClick={() => updateRefreshKey()}>Refresh</Button>;
    $[15] = updateRefreshKey;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <ViewSplitPaneContentHeader title="Metrics overview" actions={<>{t4}{t5}</>} />;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== onDateRangeChanged) {
    t7 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[20] = onDateRangeChanged;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      direction: "column",
      gap: 6
    };
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== date_range || $[24] !== source_id) {
    t9 = stringifyQuery({
      date: getDateRangeQueryParam(date_range),
      source_id: [source_id]
    });
    $[23] = date_range;
    $[24] = source_id;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = `/requests?${t9}`;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Icon icon="link" pointer={true} right={2} />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10) {
    t12 = <Button ghost={true} small={true} to={t10}>Explore{t11}</Button>;
    $[27] = t10;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== requests_status_datasets || $[30] !== requests_status_loading || $[31] !== t12) {
    t13 = <BaseMetricChart title="Requests" chart_type="bar" datasets={requests_status_datasets} datasets_type="stacked" force_legend={true} loading={requests_status_loading} action={t12} />;
    $[29] = requests_status_datasets;
    $[30] = requests_status_loading;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = overview_columns_for_breakpoints[breakpoint];
  let t15;
  if ($[33] !== t14) {
    t15 = {
      columns: t14,
      gap: 6
    };
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  let t17;
  if ($[35] !== metric_configs) {
    t16 = <MetricChart metric="requests_rate" configs={metric_configs} />;
    t17 = <MetricChart metric="requests_average_event" configs={metric_configs} />;
    $[35] = metric_configs;
    $[36] = t16;
    $[37] = t17;
  } else {
    t16 = $[36];
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t15 || $[39] !== t16 || $[40] !== t17) {
    t18 = <StyledOverviewMetricsGrid grid={t15}>{t16}{t17}</StyledOverviewMetricsGrid>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t13 || $[43] !== t18) {
    t19 = <Div flex={t8}>{t13}{t18}</Div>;
    $[42] = t13;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t19 || $[46] !== t7) {
    t20 = <ChartInteractionGroup onTimeRangeSelected={t7}>{t19}</ChartInteractionGroup>;
    $[45] = t19;
    $[46] = t7;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20 || $[49] !== t6) {
    t21 = <>{t6}{t20}</>;
    $[48] = t20;
    $[49] = t6;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
};
export const SourceOverview = t0 => {
  const $ = _c(56);
  const {
    source
  } = t0;
  const {
    source_types,
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const source_type = source_types?.[source.type];
  const auth_type = source.config.auth ? source.config.auth_type ? (source_type?.features?.VERIFICATION as SourceTypeFeatureTypesUnmanaged)?.auth?.[source.config.auth_type]?.label : source_type?.label : null;
  const t1 = `/sources/${source.id}/settings`;
  let t2;
  if ($[0] !== t1) {
    t2 = <ViewSplitPaneSidebarHeader title="Source details" action={<Button outline={true} icon="edit" to={t1}>Edit</Button>} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !!source?.disabled_at;
  const t4 = !source?.disabled_at;
  const t5 = !source?.disabled_at;
  const t6 = !source?.disabled_at ? "bolt" : "disable";
  const t7 = !source?.disabled_at ? "Active" : "Disabled";
  let t8;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5 || $[5] !== t6 || $[6] !== t7) {
    t8 = <ViewMetadataItem label="Status"><Badge neutral={t3} subtle={t4} success={t5} icon={t6}>{t7}</Badge></ViewMetadataItem>;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== source.name) {
    t9 = <ViewMetadataItem label="Name"><ViewMetadataCopyable value={source.name} wrap={true} /></ViewMetadataItem>;
    $[8] = source.name;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== source.description) {
    t10 = source.description && <ViewMetadataItem label="Description"><ViewMetadataDescription>{source.description}</ViewMetadataDescription></ViewMetadataItem>;
    $[10] = source.description;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t8 || $[14] !== t9) {
    t11 = <ViewSplitPaneSidebarSection>{t8}{t9}{t10}</ViewSplitPaneSidebarSection>;
    $[12] = t10;
    $[13] = t8;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== source_type) {
    t12 = <ViewMetadataItem label="Type">{source_type ? <Badge neutral={true} icon={source_type.icon as IconName}>{source_type.label}</Badge> : <Skeleton w={{
        px: 100
      }} h={{
        px: 20
      }} loading={true} />}</ViewMetadataItem>;
    $[16] = source_type;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== source.url) {
    t13 = <ViewMetadataItem label="URL"><ViewMetadataCopyable value={source.url} wrap={true} /></ViewMetadataItem>;
    $[18] = source.url;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== auth_type || $[21] !== source_type?.label) {
    t14 = <ViewMetadataItem label="Authentication method">{auth_type ? <Badge success={true} subtle={true}>{auth_type === source_type?.label ? "Enabled" : auth_type}</Badge> : <Badge neutral={true} subtle={true}>Disabled</Badge>}</ViewMetadataItem>;
    $[20] = auth_type;
    $[21] = source_type?.label;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== source.config.custom_response || $[24] !== source_type) {
    t15 = source_type && source_type.features.CUSTOM_RESPONSE && !source_type.features.CUSTOM_RESPONSE.managed && <ViewMetadataItem label="Custom response"><Badge neutral={true} subtle={true}>{source.config?.custom_response ? "On" : "Off"}</Badge></ViewMetadataItem>;
    $[23] = source.config.custom_response;
    $[24] = source_type;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== source.config.allowed_http_methods || $[27] !== source_type) {
    t16 = source_type && source_type.features.CUSTOM_HTTP_METHOD && !source_type.features.CUSTOM_HTTP_METHOD.managed && <ViewMetadataItem label="HTTP methods"><Div flex={{
        wrap: true,
        gap: 2
      }}>{(source.config?.allowed_http_methods || []).map(_temp2)}</Div></ViewMetadataItem>;
    $[26] = source.config.allowed_http_methods;
    $[27] = source_type;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16) {
    t17 = <ViewSplitPaneSidebarSection title="Configuration" bordered={true}>{t12}{t13}{t14}{t15}{t16}</ViewSplitPaneSidebarSection>;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== source.id) {
    t18 = <ViewMetadataItem label="Source ID"><ViewMetadataCopyable value={source.id} /></ViewMetadataItem>;
    $[35] = source.id;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== source.created_at) {
    t19 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={source.created_at} /></ViewMetadataItem>;
    $[37] = source.created_at;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== source.updated_at) {
    t20 = <ViewMetadataItem label="Last updated"><ViewMetadataDate date={source.updated_at} /></ViewMetadataItem>;
    $[39] = source.updated_at;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t18 || $[42] !== t19 || $[43] !== t20) {
    t21 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t18}{t19}{t20}</ViewSplitPaneSidebarSection>;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t11 || $[46] !== t17 || $[47] !== t2 || $[48] !== t21) {
    t22 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t2}{t11}{t17}{t21}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[45] = t11;
    $[46] = t17;
    $[47] = t2;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== source.id) {
    t23 = <StyledViewSplitPaneContent><SourceOverviewMetrics source_id={source.id} /></StyledViewSplitPaneContent>;
    $[50] = source.id;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== is_degraded_banner_active || $[53] !== t22 || $[54] !== t23) {
    t24 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t22}{t23}</StyledViewSplitPaneLayout>;
    $[52] = is_degraded_banner_active;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
};
function _temp(dataset) {
  return !!dataset;
}
function _temp2(method) {
  return <Badge key={method} neutral={true} subtle={true}>{method}</Badge>;
}